import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Image, InputNumber, Modal, Radio, Spin, Table } from 'antd'
import { useRequest } from 'ahooks'
import { useHistory } from 'react-router'
import Button from '@/components/button/button'
import SgUpload, { UploadInstance } from '@/components/upload/upload'
import { getQiniuImgUrl, getVideoPreviewUrl, getVideoUrl } from '@/config/qiniu'
import $msg from '@/utils/message'
import { IShopConfig } from '@/model/types/shop'
import { IPagination } from '@/model/types/common'
import domain from '@/api/config'
import { shopStore } from '@/store/shop'
import SettingPage, {
    SettingGrid,
    SettingPanel,
    SettingTableContainer,
} from '@/views/main/modules/member-manage/member-setting/setting-page'
import {
    addSecondaryScreenNotice,
    deleteSecondaryScreenNotice,
    getCashierSeatOccupation,
    getCashierSeatPayInfo,
    getSecondaryScreenNotice,
    getShopConfig,
    getShopPayment,
    ICashierSeat,
    IShopPayment,
    ISecondaryScreenNotice,
    makeStaffOffline,
    updateShopConfig,
} from '../api'
import PaymentSetting, { defaultPayments } from './payment-setting'
import { buildSeatPaymentUrl } from '../setting-rules'
import { SETTING_TICKET_PRINTING } from '../router'
import { WithScrollLoad } from '@/utils/utils-widget'

import './cashier-config.scss'

const NOTICE_TYPE_IMAGE = 0
const NOTICE_TYPE_VIDEO = 1
const NOTICE_SOURCE_SHOP = 1
const SEAT_PAGE_SIZE = 10

const CashierConfig: React.FC = () => {
    const history = useHistory()
    const [shopConfig, setShopConfig] = useState<IShopConfig>()
    const [notices, setNotices] = useState<ISecondaryScreenNotice[]>([])
    const [saving, setSaving] = useState(false)
    const [pendingAction, setPendingAction] = useState('')
    const [configFailed, setConfigFailed] = useState(false)
    const [seatFailed, setSeatFailed] = useState(false)
    const [seats, setSeats] = useState<ICashierSeat[]>([])
    const [seatPagination, setSeatPagination] = useState<IPagination>()
    const [seatLoading, setSeatLoading] = useState(false)
    const seatLoadingRef = useRef(false)
    const seatRequestId = useRef(0)
    const [noticeFailed, setNoticeFailed] = useState(false)
    const [paymentOpen, setPaymentOpen] = useState(false)
    const [seatOpen, setSeatOpen] = useState(false)
    const [seatCount, setSeatCount] = useState(1)
    const [seatPrice, setSeatPrice] = useState<{ fee_code: number; price: number } | null>(null)
    const [seatPriceLoading, setSeatPriceLoading] = useState(false)
    const [intervalOpen, setIntervalOpen] = useState(false)
    const [interval, setInterval] = useState(3)

    const { data: paymentData, run: loadPayment } = useRequest(getShopPayment, {
        onError: (error: any) => $msg('error', error.message || '获取支付方式失败'),
    })
    const payment = paymentData as IShopPayment | undefined

    const { loading, run: loadConfig } = useRequest(getShopConfig, {
        onSuccess: res => {
            setShopConfig(res)
            setConfigFailed(false)
        },
        onError: (e: any) => {
            setConfigFailed(true)
            $msg('error', e.message || '获取收银台设置失败')
        },
    })

    const loadSeats = useCallback(async (page: number, reset = false) => {
        if (seatLoadingRef.current && !reset) return
        const requestId = ++seatRequestId.current
        seatLoadingRef.current = true
        setSeatLoading(true)
        if (reset) {
            setSeats([])
            setSeatPagination(undefined)
            setSeatFailed(false)
        }
        try {
            const response = await getCashierSeatOccupation(page, SEAT_PAGE_SIZE)
            if (requestId !== seatRequestId.current) return
            setSeats(current => (reset ? response.data_list : [...current, ...response.data_list]))
            setSeatPagination(response.pagination)
            setSeatFailed(false)
        } catch (error) {
            if (requestId !== seatRequestId.current) return
            setSeatFailed(true)
            $msg('error', error.message || '获取收银设备失败')
        } finally {
            if (requestId === seatRequestId.current) {
                seatLoadingRef.current = false
                setSeatLoading(false)
            }
        }
    }, [])

    useEffect(() => {
        loadSeats(1, true)

        return () => {
            seatRequestId.current++
        }
    }, [loadSeats])

    const reloadSeats = () => loadSeats(1, true)
    const loadMoreSeats = () => {
        if (
            !seatLoadingRef.current &&
            !seatFailed &&
            seatPagination &&
            seatPagination.page < seatPagination.page_count
        ) {
            loadSeats(seatPagination.page + 1)
        }
    }

    const { run: loadNotices } = useRequest(getSecondaryScreenNotice, {
        onSuccess: res => {
            const images = Array.isArray(res?.image_notices) ? res.image_notices : []
            const videos = Array.isArray(res?.video_notices) ? res.video_notices : []
            setNotices([...images, ...videos].slice(0, 5))
            setNoticeFailed(false)
        },
        onError: (e: any) => {
            setNoticeFailed(true)
            $msg('error', e.message || '获取副屏公告失败')
        },
    })

    const saveConfig = async (params: Partial<IShopConfig>) => {
        if (!shopConfig || saving) return
        setSaving(true)
        try {
            await updateShopConfig(params)
            setShopConfig(current => ({ ...(current as IShopConfig), ...params }))
            $msg('success', '设置成功')

            return true
        } catch (e) {
            $msg('error', e.message || '设置失败')

            return false
        } finally {
            setSaving(false)
        }
    }

    const offline = (row: ICashierSeat) => {
        const staffId = row.occupier_staff?.id
        if (!staffId || !row.cashier_sn) return
        Modal.confirm({
            title: '确认强制下线？',
            content: `设备 ${row.cashier_sn || '--'} 的收银员将退出当前收银台。`,
            okText: '确认下线',
            cancelText: '取消',
            onOk: async () => {
                setPendingAction(`seat-${row.cashier_sn}`)
                try {
                    await makeStaffOffline(row.cashier_sn, staffId)
                    $msg('success', '已下线')
                    reloadSeats()
                } catch (e) {
                    $msg('error', e.message || '下线失败')
                } finally {
                    setPendingAction('')
                }
            },
        })
    }

    const uploadNotice = (res: any, upload: UploadInstance) => {
        const key = res && (res.key || res.hash)
        if (!key || notices.length >= 5) {
            setPendingAction('')

            return
        }
        if (upload.filetype !== 1 && upload.filetype !== 2) {
            $msg('error', '仅支持图片或视频公告')
            setPendingAction('')

            return
        }
        setPendingAction('upload')
        addSecondaryScreenNotice({
            secondary_screen_notice_list: [
                {
                    qiniu_key: key,
                    notice_type: upload.filetype === 2 ? NOTICE_TYPE_VIDEO : NOTICE_TYPE_IMAGE,
                    material_info: {
                        uid: upload.uid || `${Date.now()}-${key}`,
                        material_type: upload.filetype,
                        name: upload.name || key,
                        url: key,
                        size: Math.ceil((upload.sizeUnit || 0) / 1024),
                        duration: upload.filetype === 2 ? upload.duration || null : null,
                        proportion: upload.filetype === 1 ? upload.proportion || null : null,
                    },
                },
            ],
        })
            .then(() => {
                $msg('success', '添加成功')
                loadNotices()
            })
            .catch((e: any) => $msg('error', e.message || '添加失败'))
            .finally(() => setPendingAction(''))
    }

    const removeNotice = (item: ISecondaryScreenNotice) => {
        const noticeId = item.id
        if (item.notice_source !== NOTICE_SOURCE_SHOP || noticeId == null) return
        Modal.confirm({
            title: '删除副屏公告？',
            content: '删除后将不再在副屏展示该素材。',
            okText: '删除',
            okType: 'danger',
            cancelText: '取消',
            onOk: async () => {
                setPendingAction(`notice-${noticeId}`)
                try {
                    await deleteSecondaryScreenNotice({ secondary_screen_notice_id: noticeId })
                    $msg('success', '删除成功')
                    loadNotices()
                } catch (e) {
                    $msg('error', e.message || '删除失败')
                } finally {
                    setPendingAction('')
                }
            },
        })
    }

    const openSeatPurchase = async () => {
        setSeatCount(1)
        setSeatPrice(null)
        setSeatOpen(true)
        setSeatPriceLoading(true)
        try {
            const response = await getCashierSeatPayInfo()
            const fee = response.fee_infos?.[0]
            if (!fee || fee.status !== 1) throw new Error('收银台位暂不可购买')
            setSeatPrice({ fee_code: fee.fee_code, price: Number(fee.price) })
        } catch (error) {
            $msg('error', error.message || '获取台位价格失败')
        } finally {
            setSeatPriceLoading(false)
        }
    }

    const purchaseSeat = () => {
        const shopInfo = shopStore.shopInfo
        if (shopInfo?.from_sunmi) {
            $msg('error', '请联系您的服务商进行购买，如有疑问请拨打400-6666-509')

            return
        }
        if (!domain.center || !shopInfo || !seatPrice || !Number.isInteger(seatCount) || seatCount < 1) {
            $msg('error', '购买信息不完整，请稍后重试')

            return
        }
        window.location.href = buildSeatPaymentUrl(domain.center, shopInfo, seatPrice.fee_code, seatCount)
    }

    const enabledDefaults = defaultPayments.filter(item => payment?.paymentconfig?.payment_list?.includes(item.id))
    const enabledCustom = (payment?.custom_payment_config || []).filter(item => item.status === 1)

    return (
        <SettingPage title="收银台设置" className="store-setting-page cashier-config">
            <div className="store-setting-page__heading">
                <h1 className="store-setting-page__title">收银台设置</h1>
                <p className="store-setting-page__subtitle">按收款与打印、设备与展示分组管理收银台。</p>
            </div>
            <Spin spinning={loading}>
                <SettingGrid>
                    <div className="cashier-config__section-heading">
                        <h2>收款与打印</h2>
                        <span>管理收款金额、支付方式和收银小票</span>
                    </div>
                    <SettingPanel title="收款规则" desc="修改后将应用于后续收银订单。" wide>
                        {configFailed && (
                            <div className="store-setting-page__error" role="alert">
                                收款规则加载失败。
                                <Button types="text" onClick={() => loadConfig()}>
                                    重新加载
                                </Button>
                            </div>
                        )}
                        <div className="store-setting-page__row">
                            <span className="store-setting-page__label">订单总额精度</span>
                            <Radio.Group
                                value={shopConfig?.precision}
                                disabled={!shopConfig || saving}
                                onChange={e => saveConfig({ precision: e.target.value })}
                            >
                                <Radio value={1}>精确到分（0.00）</Radio>
                                <Radio value={2}>精确到角（0.0）</Radio>
                                <Radio value={3}>精确到元（0）</Radio>
                            </Radio.Group>
                        </div>
                        <div className="store-setting-page__row">
                            <span className="store-setting-page__label">精度处理方式</span>
                            <div className="cashier-config__rounding">
                                <Radio.Group
                                    className="cashier-config__radios"
                                    value={shopConfig?.rounding_method}
                                    disabled={!shopConfig || saving}
                                    onChange={e => saveConfig({ rounding_method: e.target.value })}
                                >
                                    <Radio value={1}>四舍五入</Radio>
                                    <Radio value={2}>进一法</Radio>
                                    <Radio value={3}>去尾法</Radio>
                                </Radio.Group>
                                <span className="store-setting-page__hint">
                                    精确到角时，9.13 元分别处理为 9.1 元、9.2 元、9.1 元。
                                </span>
                            </div>
                        </div>
                    </SettingPanel>

                    <SettingPanel title="支付方式与小票" desc="选择收银支付方式，设置交易凭证。" wide>
                        <div className="cashier-config__payment-overview">
                            <div className="cashier-config__payment-overview-head">
                                <div>
                                    <strong>收银可用方式</strong>
                                    <span>已开启 {enabledDefaults.length + enabledCustom.length} 种支付方式</span>
                                </div>
                                <Button types="text" onClick={() => setPaymentOpen(true)}>
                                    管理支付方式
                                </Button>
                            </div>
                            <div className="cashier-config__payment-tags">
                                {enabledDefaults.map(item => (
                                    <span className="cashier-config__payment-tag" key={`default-${item.id}`}>
                                        {item.name}
                                    </span>
                                ))}
                                {enabledCustom.map(item => (
                                    <span className="cashier-config__payment-tag" key={`custom-${item.id}`}>
                                        {item.name}
                                    </span>
                                ))}
                                {!payment && (
                                    <span className="cashier-config__payment-placeholder">正在加载支付方式…</span>
                                )}
                                {payment && !enabledDefaults.length && !enabledCustom.length && (
                                    <span className="cashier-config__payment-placeholder">暂无已开启的支付方式</span>
                                )}
                            </div>
                        </div>
                        <div className="store-setting-page__row cashier-config__receipt-row">
                            <span className="store-setting-page__label">收银小票</span>
                            <span className="store-setting-page__value">设置小票底部文字、二维码和图片</span>
                            <Button types="text" onClick={() => history.push(SETTING_TICKET_PRINTING)}>
                                设置小票
                            </Button>
                        </div>
                    </SettingPanel>

                    <div className="cashier-config__section-heading cashier-config__section-heading--separated">
                        <h2>设备与展示</h2>
                        <span>查看收银台位和副屏公告</span>
                    </div>
                    <SettingPanel
                        title="收银设备"
                        desc="查看台位使用情况，必要时可强制下线。"
                        wide
                        extra={
                            <span className="cashier-config__seat">
                                <span>收银台位：{shopConfig?.cashier_seat ?? '--'} 台</span>
                                <Button types="text" onClick={openSeatPurchase}>
                                    添加台位
                                </Button>
                            </span>
                        }
                    >
                        {seatFailed && (
                            <div className="store-setting-page__error" role="alert">
                                收银设备加载失败。
                                <Button types="text" onClick={reloadSeats}>
                                    重新加载
                                </Button>
                            </div>
                        )}
                        <SettingTableContainer className="cashier-config__device-table">
                            <WithScrollLoad
                                className="cashier-config__device-scroll"
                                scrollloadhandle={{
                                    loadMore: loadMoreSeats,
                                    loadingMore:
                                        seatLoading ||
                                        seatFailed ||
                                        !seatPagination ||
                                        seatPagination.page >= seatPagination.page_count,
                                }}
                            >
                                <Table<ICashierSeat>
                                    rowKey="cashier_sn"
                                    loading={seatLoading}
                                    dataSource={seats}
                                    pagination={false}
                                    columns={[
                                        {
                                            title: '#',
                                            width: 50,
                                            render: (_v, _r, index) => index + 1,
                                        },
                                        { title: '设备号', dataIndex: 'cashier_sn', render: v => v || '--' },
                                        {
                                            title: '当前状态',
                                            render: (_v, row) => {
                                                const online = row.occupier_staff && row.occupier_staff.user

                                                return (
                                                    <div>
                                                        <span
                                                            className={`store-setting-page__status${
                                                                online ? ' store-setting-page__status--active' : ''
                                                            }`}
                                                        >
                                                            {online ? '在线' : '离线'}
                                                        </span>
                                                        {online && (
                                                            <span className="cashier-config__staff">
                                                                收银员：
                                                                {row.occupier_staff?.user?.realname ||
                                                                    row.occupier_staff?.user?.nickname ||
                                                                    '--'}
                                                            </span>
                                                        )}
                                                    </div>
                                                )
                                            },
                                        },
                                        {
                                            title: '操作',
                                            width: 150,
                                            align: 'center',
                                            render: (_v, row) =>
                                                row.occupier_staff?.id && row.cashier_sn ? (
                                                    <div className="cashier-config__offline-action">
                                                        <Button
                                                            types="text"
                                                            className="cashier-config__offline"
                                                            disabled={!!pendingAction}
                                                            onClick={() => offline(row)}
                                                        >
                                                            强制下线
                                                        </Button>
                                                    </div>
                                                ) : null,
                                        },
                                    ]}
                                />
                            </WithScrollLoad>
                        </SettingTableContainer>
                    </SettingPanel>

                    <SettingPanel title="副屏公告" desc={`支持图片和视频，最多 5 个；当前 ${notices.length}/5。`} wide>
                        {noticeFailed && (
                            <div className="store-setting-page__error" role="alert">
                                副屏公告加载失败。
                                <Button types="text" onClick={() => loadNotices()}>
                                    重新加载
                                </Button>
                            </div>
                        )}
                        <div className="cashier-config__notice-list">
                            {notices.map(item => (
                                <div
                                    className="cashier-config__notice-item"
                                    key={`${item.notice_source}-${item.id || item.qiniu_key}`}
                                >
                                    {item.notice_type === NOTICE_TYPE_VIDEO ? (
                                        <a href={getVideoUrl(item.qiniu_key)} target="_blank" rel="noopener noreferrer">
                                            <img
                                                className="cashier-config__notice-img"
                                                src={getVideoPreviewUrl(item.qiniu_key, 240)}
                                                alt="视频公告预览"
                                            />
                                        </a>
                                    ) : (
                                        <Image
                                            className="cashier-config__notice-img"
                                            src={getQiniuImgUrl(item.qiniu_key, 240, 160)}
                                        />
                                    )}
                                    <span className="cashier-config__notice-kind">
                                        {item.notice_type === NOTICE_TYPE_VIDEO ? '视频' : '图片'}
                                    </span>
                                    {item.notice_source === NOTICE_SOURCE_SHOP && item.id != null ? (
                                        <Button
                                            types="text"
                                            className="cashier-config__notice-delete"
                                            disabled={!!pendingAction}
                                            onClick={() => removeNotice(item)}
                                        >
                                            删除
                                        </Button>
                                    ) : (
                                        <span className="cashier-config__notice-kind">总部公告</span>
                                    )}
                                </div>
                            ))}
                            {notices.length === 0 && <span className="cashier-config__empty">暂无副屏公告</span>}
                        </div>
                        {notices.length < 5 && !noticeFailed && (
                            <div className="cashier-config__upload-actions">
                                <SgUpload
                                    className={pendingAction ? 'store-setting-page__upload--disabled' : ''}
                                    accept="image/png,image/jpeg,video/mp4,video/quicktime"
                                    onLoad={() => setPendingAction('upload')}
                                    onError={() => setPendingAction('')}
                                    onSuccess={uploadNotice}
                                >
                                    <Button types="primary" loading={!!pendingAction} disabled={!!pendingAction}>
                                        添加图片或视频
                                    </Button>
                                </SgUpload>
                            </div>
                        )}
                        <div className="store-setting-page__row">
                            <span className="store-setting-page__label">轮播时间</span>
                            <span className="store-setting-page__value">
                                {shopConfig?.notice_time_interval || 3} 秒
                            </span>
                            <Button
                                types="text"
                                disabled={!shopConfig}
                                onClick={() => {
                                    setInterval(shopConfig?.notice_time_interval || 3)
                                    setIntervalOpen(true)
                                }}
                            >
                                编辑
                            </Button>
                        </div>
                    </SettingPanel>
                </SettingGrid>
            </Spin>
            <Modal
                title="支付方式设置"
                visible={paymentOpen}
                footer={null}
                width={600}
                className="cashier-config__payment-modal"
                destroyOnClose
                onCancel={() => {
                    setPaymentOpen(false)
                    loadPayment()
                }}
            >
                <PaymentSetting />
            </Modal>
            <Modal
                title="添加收银台位"
                visible={seatOpen}
                confirmLoading={seatPriceLoading}
                okButtonProps={{ disabled: !seatPrice }}
                okText="立即支付"
                onOk={purchaseSeat}
                onCancel={() => setSeatOpen(false)}
            >
                <Spin spinning={seatPriceLoading}>
                    <div className="store-setting-page__row cashier-config__purchase-row">
                        <span>购买服务</span>
                        <span>收银台位（一年）</span>
                    </div>
                    <div className="store-setting-page__row cashier-config__purchase-row">
                        <span>单价</span>
                        <span>{seatPrice ? `¥${seatPrice.price.toFixed(2)}/位` : '--'}</span>
                    </div>
                    <div className="store-setting-page__row cashier-config__purchase-row">
                        <span>购买数量</span>
                        <InputNumber
                            min={1}
                            precision={0}
                            value={seatCount}
                            onChange={value => setSeatCount(value || 1)}
                        />
                    </div>
                    <div className="store-setting-page__row cashier-config__purchase-row">
                        <span>订单总价</span>
                        <strong>{seatPrice ? `¥${(seatPrice.price * seatCount).toFixed(2)}` : '--'}</strong>
                    </div>
                </Spin>
            </Modal>
            <Modal
                title="轮播图播放时间间隔"
                visible={intervalOpen}
                confirmLoading={saving}
                okText="保存"
                onOk={async () => {
                    if (await saveConfig({ notice_time_interval: interval })) setIntervalOpen(false)
                }}
                onCancel={() => setIntervalOpen(false)}
            >
                <Radio.Group value={interval} onChange={event => setInterval(event.target.value)}>
                    {[3, 5, 10, 15, 20, 25].map(seconds => (
                        <Radio key={seconds} value={seconds}>
                            {seconds} 秒
                        </Radio>
                    ))}
                </Radio.Group>
            </Modal>
        </SettingPage>
    )
}

export default CashierConfig
